import type {
  BuyerCandidate,
  ExitOpportunity,
  ExitPlan,
  ExitProject,
  ExitTransaction,
  ExitTransactionAllocation,
  ProjectStage,
  TransactionStage,
} from './types';

export type ExitProjectApi = {
  id: string;
  opportunityId?: string;
  companyId: string;
  fundId: string;
  fundUuid?: string;
  name: string;
  owner: string;
  stage: ProjectStage;
  currentHoldingRatio: number;
  investmentCost: number;
  latestValuation: number;
  riskLevel: ExitProject['riskLevel'];
  nextAction: string;
  plan: {
    method: string;
    exitType: string;
    targetExitAmount: number;
    /** 定价估值（万元）：退出方案决策依据，后端 inv_exit_project.exit_valuation。 */
    exitValuation?: number;
    /** 估值方法（收益法/市场法/成本法/评估报告）。 */
    valuationMethod?: string;
    /** 估值基准日。 */
    valuationDate?: string;
    /** 定价依据（评估报告/市场法/回购条款/清算评估）。 */
    pricingBasis?: string;
    /** 计划交割日。 */
    plannedTransactionDate?: string;
  };
  selectedBuyerId?: string;
  allowedNextStages?: ProjectStage[];
  canResume?: boolean;
};

export type ExitTransactionApi = {
  id: string;
  projectId: string;
  projectName: string;
  companyId: string;
  stage: TransactionStage;
  /** 退出方式：老股转让 / 股票变卖 / 回购 / IPO / 并购 / 清算 / 其他（对应后端 ExitMethod）。 */
  exitMethod?: string | null;
  seller?: string;
  buyerName: string;
  buyerId?: string;
  buyerOrganizationUuid?: string;
  buyerContactPersonUuid?: string;
  buyerOrganization?: {
    id: string;
    name: string;
    shortName?: string;
    orgType?: string;
    industry?: string;
    region?: string;
  };
  buyerContact?: {
    id: string;
    name: string;
    phone?: string;
    title?: string;
  };
  dealAmount: number;
  transactionFee: number;
  taxAmount: number;
  netProceeds: number;
  currency: string;
  realizedGain: number;
  soldHolding: number;
  remainingHolding: number;
  owner: string;
  writebackCompleted: boolean;
  legalClosingCompleted?: boolean;
  settlementCompleted?: boolean;
  allowedNextStages?: TransactionStage[];
  canResume?: boolean;
  allocations?: ExitTransactionAllocationApi[];
};

export type ExitTransactionAllocationApi = {
  id: string;
  fundUuid?: string;
  fundName: string;
  investmentRelationUuid: string;
  soldHolding: number;
  costDeduction: number;
  consideration: number;
  transactionFee: number;
  taxAmount: number;
  realizedGain: number;
};

const mapApiExitTransactionAllocation = (
  allocation: ExitTransactionAllocationApi,
): ExitTransactionAllocation => ({
  id: allocation.id,
  fundUuid: allocation.fundUuid,
  fundName: allocation.fundName,
  investmentRelationUuid: allocation.investmentRelationUuid,
  soldHolding: allocation.soldHolding,
  costDeduction: allocation.costDeduction,
  consideration: allocation.consideration,
  transactionFee: allocation.transactionFee,
  taxAmount: allocation.taxAmount,
  realizedGain: allocation.realizedGain,
});

const defaultExitPlan = (dto: ExitProjectApi): ExitPlan => ({
  method: dto.plan.method,
  alternatives: [],
  exitType: dto.plan.exitType === '全部退出' ? '全部退出' : '部分退出',
  targetSellRatio: dto.currentHoldingRatio,
  // 定价估值：后端有值用真值，否则回落最新估值；不再合成假值
  targetValuation: dto.plan.exitValuation ?? dto.latestValuation,
  minimumValuation: null,
  targetCompletionDate: dto.plan.plannedTransactionDate ?? '',
  reason: dto.plan.pricingBasis ?? '',
  targetExitAmount: dto.plan.targetExitAmount,
  exitValuation: dto.plan.exitValuation ?? null,
  valuationMethod: dto.plan.valuationMethod ?? null,
  valuationDate: dto.plan.valuationDate ?? null,
  pricingBasis: dto.plan.pricingBasis ?? null,
  plannedTransactionDate: dto.plan.plannedTransactionDate ?? null,
});

export const mapApiExitProject = (dto: ExitProjectApi): ExitProject => ({
  id: dto.id,
  opportunityId: dto.opportunityId,
  companyId: dto.companyId,
  fundId: dto.fundUuid || dto.fundId,
  fundUuid: dto.fundUuid,
  name: dto.name,
  owner: dto.owner,
  stage: dto.stage,
  currentHoldingRatio: dto.currentHoldingRatio,
  investmentCost: dto.investmentCost,
  latestValuation: dto.latestValuation,
  riskLevel: dto.riskLevel,
  nextAction: dto.nextAction,
  plan: defaultExitPlan(dto),
  allowedNextStages: dto.allowedNextStages,
  canResume: dto.canResume,
  selectedBuyerId: dto.selectedBuyerId,
});

export const mapApiExitTransaction = (
  dto: ExitTransactionApi,
): ExitTransaction => ({
  id: dto.id,
  projectId: dto.projectId,
  companyId: dto.companyId,
  buyerId: dto.buyerId ?? `buyer-${dto.id}`,
  buyerName: dto.buyerName,
  name: dto.projectName,
  seller:
    dto.allocations
      ?.map((allocation) => allocation.fundName)
      .filter(Boolean)
      .join('、') || dto.seller || '—',
  buyerOrganizationId: dto.buyerOrganizationUuid,
  buyerContactPersonId: dto.buyerContactPersonUuid,
  buyerOrganization: dto.buyerOrganization
    ? {
        id: dto.buyerOrganization.id,
        name: dto.buyerOrganization.name,
        shortName: dto.buyerOrganization.shortName,
      }
    : undefined,
  buyerContact: dto.buyerContact
    ? {
        id: dto.buyerContact.id,
        name: dto.buyerContact.name,
        phone: dto.buyerContact.phone,
      }
    : undefined,
  stage: dto.stage,
  exitMethod: dto.exitMethod ?? undefined,
  soldRatio: dto.soldHolding,
  consideration: dto.netProceeds,
  grossConsideration: dto.dealAmount,
  transactionFee: dto.transactionFee,
  taxAmount: dto.taxAmount,
  currency: dto.currency,
  realizedGain: dto.realizedGain,
  owner: dto.owner,
  writebackCompleted: dto.writebackCompleted,
  legalClosingCompleted: dto.legalClosingCompleted,
  settlementCompleted: dto.settlementCompleted,
  allowedNextStages: dto.allowedNextStages,
  canResume: dto.canResume,
  remainingHolding: dto.remainingHolding,
  allocations: dto.allocations?.map(mapApiExitTransactionAllocation),
});

export const mapApiTransactionToBuyer = (
  transaction: ExitTransaction,
): BuyerCandidate | undefined => {
  if (
    transaction.buyerId ||
    (!transaction.buyerOrganizationId && !transaction.buyerOrganization)
  ) {
    return undefined;
  }
  const contactLabel = transaction.buyerContact
    ? transaction.buyerContact.phone
      ? `${transaction.buyerContact.name}（${transaction.buyerContact.phone}）`
      : transaction.buyerContact.name
    : '待补充';
  return {
    id: transaction.buyerId,
    projectId: transaction.projectId,
    name:
      transaction.buyerOrganization?.name ??
      transaction.buyerName ??
      transaction.seller,
    organizationId: transaction.buyerOrganizationId,
    contactPersonId: transaction.buyerContactPersonId,
    type: '产业集团',
    source: 'CRM 主数据',
    recommender: transaction.owner,
    contact: contactLabel,
    stage: '待接洽',
    intentionScore: 3,
    purchaseRatio: transaction.soldRatio || 0.5,
    latestQuote: transaction.consideration,
    ndaStatus: '未签署',
    materialStatus: '未提交',
    lastContactAt: '-',
    nextAction: '安排首次沟通',
  };
};

export type { ExitOpportunity };
